"use client";

import type { ReactNode } from "react";
import { SkeletonElement } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import Image from "next/image";

const AVATAR_COLOR_CLASSES = [
  "bg-[var(--color-avatar-0)]",
  "bg-[var(--color-avatar-1)]",
  "bg-[var(--color-avatar-2)]",
  "bg-[var(--color-avatar-3)]",
  "bg-[var(--color-avatar-4)]",
  "bg-[var(--color-avatar-5)]",
  "bg-[var(--color-avatar-6)]",
  "bg-[var(--color-avatar-7)]",
] as const;

const normalizeIdentifier = (identifier?: string | number | null) => {
  if (identifier === null || identifier === undefined) {
    return "";
  }

  return String(identifier);
};

const getAvatarColorClass = (identifier?: string | number | null) => {
  const normalizedIdentifier = normalizeIdentifier(identifier);

  if (!normalizedIdentifier) {
    return AVATAR_COLOR_CLASSES[0];
  }

  const charCodeSum = [...normalizedIdentifier].reduce(
    (accumulator, character) => accumulator + character.charCodeAt(0),
    0,
  );
  const colorIndex = charCodeSum % AVATAR_COLOR_CLASSES.length;

  return AVATAR_COLOR_CLASSES[colorIndex];
};

const getInitial = (identifier?: string | number | null) => {
  const normalizedIdentifier = normalizeIdentifier(identifier).trim();

  if (!normalizedIdentifier) {
    return "?";
  }

  return normalizedIdentifier[0]?.toUpperCase();
};

export interface UserAvatarProps {
  identifier?: string | number | null;
  src?: string;
  alt?: string;
  size?: number;
  className?: string;
  imageClassName?: string;
  fallback?: ReactNode;
}

export const UserAvatar = ({
  identifier,
  src,
  alt = "User avatar",
  size = 40,
  className,
  imageClassName,
  fallback,
}: UserAvatarProps) => {
  const normalizedIdentifier = normalizeIdentifier(identifier);
  const hasIdentifier = normalizedIdentifier.length > 0;
  const colorClass = hasIdentifier ? getAvatarColorClass(identifier) : "";

  return (
    <div
      className={cn(
        "relative inline-flex items-center justify-center overflow-hidden rounded-full text-white",
        // The box is class-sized, and the image below fills it. Without a size the
        // percentage resolves to auto and a large `src` renders at its natural
        // dimensions, so default to 40px; any caller w-*/h-* still wins via twMerge.
        "h-10 w-10",
        colorClass,
        className,
      )}
    >
      {!hasIdentifier ? (
        <SkeletonElement className="h-full w-full rounded-full" />
      ) : src ? (
        <Image
          src={src}
          width={size}
          height={size}
          alt={alt}
          className={cn("h-full w-full object-cover", imageClassName)}
        />
      ) : (
        (fallback ?? <span className="text-sm font-semibold uppercase">{getInitial(identifier)}</span>)
      )}
    </div>
  );
};
